import React, { useContext, CSSProperties } from 'react';
import { Button, ButtonProps, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { OrganizationThemeContext } from '../../../lib/context/OrganizationThemeContext';

const useStyles = makeStyles((theme: Theme) => ({
  outlinedPrimary: {
    color: theme.palette.primary.contrastText,
    '&:hover': {
      color: theme.palette.primary.contrastText,
    },
  },
}));

export interface ThemableButtonProps extends Omit<ButtonProps, 'color'> {
  component?: React.ElementType;
  rel?: string;
  styles?: CSSProperties;
  target?: string;
  dataTestId?: string;
}

const ThemableButton: React.FC<ThemableButtonProps> = (
  props: ThemableButtonProps
) => {
  const { hasCustomTheme, textStyles } = useContext(OrganizationThemeContext);

  const classes = useStyles();

  const {
    children,
    component = 'button',
    disabled,
    styles,
    variant = 'outlined',
    dataTestId,
    ...buttonProps
  } = props;

  let color: ButtonProps['color'] = 'secondary';
  let textStylesOverwrites: CSSProperties | undefined = textStyles;

  switch (variant) {
    case 'contained': {
      textStylesOverwrites = undefined;
      color = hasCustomTheme ? 'primary' : 'secondary';
      break;
    }
    case 'outlined': {
      color = hasCustomTheme ? 'primary' : 'secondary';
      break;
    }
  }

  /**
   * The color overwrite must be removed when the button is disabled, as it
   * should show the gray-ish color instead of the overwritten organization
   * color, which should be the org's secondary color
   */
  if (disabled && textStylesOverwrites) {
    textStylesOverwrites.color = undefined;
  }

  return (
    <Button
      {...buttonProps}
      classes={{
        ...buttonProps.classes,
        containedPrimary: classes.outlinedPrimary,
      }}
      color={color}
      component={component}
      disabled={disabled}
      style={{ ...textStylesOverwrites, ...styles }}
      variant={variant}
      data-testid={dataTestId}
    >
      {children}
    </Button>
  );
};

export default ThemableButton;
